import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Debounces the provided value with the provided delay. Returns the debounced
 * value and an optional setter to set the value immediately and clear the
 * current debounce.
 *
 * @param value value to debounce
 * @param delay debounce duration in milliseconds
 *
 * @returns a tuple with value and immediate setter
 */
const useDebounce = <T extends {}>(value: T, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const timeout = useRef<number | null>(null);

  useEffect(() => {
    if (timeout.current) clearTimeout(timeout.current);

    timeout.current = window.setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      if (timeout.current) clearTimeout(timeout.current);
    };
  }, [value, delay]);

  const setImmediate = useCallback((immediateValue: T) => {
    if (timeout.current) clearTimeout(timeout.current);

    setDebouncedValue(immediateValue);
  }, []);

  return [debouncedValue, setImmediate] as const;
};

export { useDebounce };
